import {Input, Modal, Select, Spin} from 'antd'
import {
  doc_editCodiDocument_Api,
  doc_setPrevCodiDocument_Api,
  docs_fetchAllCodiDocuments_Api,
  docs_mergeCodiAmendments_Api,
  docs_reGenerateSyncCodiDocument_Api,
  docs_removeCodiDocument_Api,
  docs_updateSyncReformat_Api,
} from 'apis'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import Pure from 'components/Pure'
import {Formik} from 'formik'
import filterOption from 'helpers/filterOption'
import getTitle from 'helpers/getTitle'
import _, {debounce} from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {LocalContext} from 'modules/local/LocalContext'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect, useMemo, useState} from 'react'
import {BsChevronRight} from 'react-icons/bs'
import CodiDocumentContext, {UseConfirmDeleteAction,} from 'views/Document/CodiDocumentContext'
import AntdConfigWrapper from 'views/AppLocals/custom/AntdConfigWrapper'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {showConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {AdvancedActionGroups} from 'views/NewTemplate/components/AdvancedActionGroups'
import {confirmActionDialog, DefaultActionButton,} from 'views/NewTemplate/components/AdvancedActionGroups/ActionGroup'
import {getResponseItems, NullAsDefault, shallowDiff} from 'views/Shared'
import {docs_updateSyncInfoCodiDocument_Api} from '../../../../apis'
import {formatDate} from "../../../../helpers/datetimeHelpers";

const ActionTypes = Object.freeze({
    MERGE_AMENDMENTS: 'merge-amendments',
    REMOVE_CODI: 'remove-codi',
    SET_PREV_CODI: 'set-pre-codi',
    PUBLISH_CODI: 'publish-codi',
    UPDATE_OVERVIEW_INFO: 'update-overview-info',
})

const PrevCodiSelect = ({
                            name,
                            value,
                            onChange,
                            debounceTimeout = 500,
                            getInfoOptions = (options) => options,
                        }) => {
    const t = useTranslate()

    const [options, setOptions] = useState([])

    const {isLoading = false, handleAsyncAction = NullAsDefault} = useAsyncAction({
        apiInfo: docs_fetchAllCodiDocuments_Api,
        onError: notifyOnError(t),
        onSuccess: (__, data) => {
            const items = Array.from(getResponseItems(data, 'response.data.data'))
            setOptions(getInfoOptions(items))
        },
    })

    useEffect(() => {
        handleAsyncAction()
    }, [])

    const debounceFetcher = useMemo(() => {
        const loadOptions = (keyword) => {
            setOptions([])
            handleAsyncAction({
                keyword,
            })
        }
        return debounce(loadOptions, debounceTimeout)
    }, [handleAsyncAction, debounceTimeout])

    return (
        <SelectWidget
            className="w-full"
            showSearch={true}
            allowClear={true}
            filterOption={false}
            loading={!!isLoading}
            optionLabelProp="label"
            onSearch={debounceFetcher}
            placeholder={t('codi document')}
            onChange={(value) => {
                onChange(createValue(name, value))
            }}
            value={!!isLoading ? undefined : value}
            notFoundContent={isLoading ? <Spin size="small"/> : null}>
            {options.map((item) => {
                const {id: value} = item
                const {eff_date, document_no, document} = item

                return (
                    <Select.Option
                        key={value}
                        value={value}
                        label={<span className="font-semibold">{getTitle(item)}</span>}>
                        <div className="flex flex-col">
              <span className="font-bold text-xs text-primary italic">
                <span className="font-semibold text-color-000">
                  {eff_date}
                    {' - '}
                </span>
                  {getTitle(item)}
              </span>
                            <span className="text-2xs italic leading-tight space-x-1">
                <span className="font-medium text-color-100">
                  {_.get(document, 'issued_place.title')} {' - '}
                </span>
                <span className="font-medium text-color-100">
                  <span className="font-light text-color-300">
                    {t('document no')}
                      {' : '}
                  </span>
                    {document_no}
                </span>
              </span>
                        </div>
                    </Select.Option>
                )
            })}
        </SelectWidget>
    )
}

const formSchema = {
    title: {
        title: 'title',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'title',
                }),
            },
        ],
    },
    description: {
        title: 'description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    rows: 5,
                    placeholder: 'description',
                    Component: Input.TextArea,
                }),
            },
        ],
    },
    document_no: {
        title: 'document number',
        children: [
            {
                name: 'document_no',
                component: createControlledFormField({
                    placeholder: 'document number',
                    disabled: true,
                }),
            },
        ],
    },
    prev_codi_document: {
        title: 'document',
        children: [
            {
                name: 'prev_id',
                component: PrevCodiSelect,
            },
        ],
    },
    categories: {
        title: 'categories',
        children: [
            {
                name: 'categories',
                component: ({name, value, onChange}) => (
                    <Pure input={[value]}>
                        <Translate>
                            {(t) => (
                                <LocalContext.Consumer>
                                    {({appConfig}) => {
                                        const {categories} = appConfig || {}
                                        const options = Array.from(categories || []).map(
                                            (category) => ({
                                                label: t(getTitle(category)),
                                                value: category?.id,
                                                ...category,
                                            })
                                        )
                                        return (
                                            <SelectWidget
                                                value={value}
                                                mode="multiple"
                                                className="w-full"
                                                showSearch={true}
                                                allowClear={true}
                                                options={options}
                                                filterOption={filterOption}
                                                onChange={(value, option) => {
                                                    onChange(createValue(name, value))
                                                }}
                                            />
                                        )
                                    }}
                                </LocalContext.Consumer>
                            )}
                        </Translate>
                    </Pure>
                ),
            },
        ],
    },
}

const UpdateInfo = ({item, onSubmit}) => {
    const t = useTranslate()

    const validate = (values) => {
        const {title} = values || {}

        let errors = {}

        if (_.isEmpty(title)) {
            errors.title = t('required field')
        }
        return errors
    }

    const initialValues = useMemo(() => {
        const {categories} = item || {}
        return {
            ...item,
            title: _.get(item, 'og_title'),
            categories: _.isArray(categories)
                ? categories.map((category) => category?.id)
                : [],
            document_no: _.get(item, 'document.document_no'),
        }
    }, [item])

    return (
        <Formik
            validate={validate}
            onSubmit={(values) => {
                const changed = shallowDiff(values, initialValues)
                const {categories, ...rest} = changed || {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            categories: categories
                                ? Array.from(categories).join(',')
                                : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}
            enableReinitialize={true}
            initialValues={initialValues}>
            <div className="flex flex-col space-y-2">
                <AntdConfigWrapper>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.description,
                            formSchema.categories,
                            formSchema.document_no,
                        ]}
                    />
                </AntdConfigWrapper>
                <FormActionBar submitText="save"/>
            </div>
        </Formik>
    )
}

const PreCodiModal = ({item, onSubmit}) => {
    const validate = undefined

    const initialValues = useMemo(() => {
        const {} = item || {}
        return {
            ...item,
        }
    }, [item])

    const document = _.get(item, 'document')

    return (
        <Formik
            validate={validate}
            onSubmit={(values) => {
                const changed = shallowDiff(values, initialValues)
                onSubmit(_.omitBy(changed, _.isUndefined))
            }}
            enableReinitialize={true}
            initialValues={initialValues}>
            <div className="flex flex-col gap-3">
                <div className="p-3 background border border-color-50 rounded-lg">
                    <div className="flex flex-col">
            <span className="text-primary font-bold uppercase">
              {getTitle(item)}
            </span>
                        <span className="flex items-baseline gap-1 text-xs italic">
              <span className="text-color-100 font-medium">
                {_.get(document, 'issued_place.title')}
              </span>
              <span className="text-color-400 font-light">
                {' - '}
                  {formatDate(_.get(item, 'eff_date'), 'DD/MM/YYYY')}
              </span>
            </span>
                    </div>
                    <div className="text-sm text-color-300 font-light italic italic mt-3">
                        {_.get(item, 'description')}
                    </div>
                </div>
                <AntdConfigWrapper>
                    <FieldsFactory formSchema={[formSchema.prev_codi_document]}/>
                </AntdConfigWrapper>
                <FormActionBar submitText="save"/>
            </div>
        </Formik>
    )
}

const UseUpdateCodiDocument = ({
                                   children,
                                   codi_document_id,
                                   onSuccess = NullAsDefault,
                               }) => {
    const t = useTranslate()
    const asyn_data = useAsyncAction({
        apiInfo: doc_editCodiDocument_Api,
        query: bindQueryParam({id: codi_document_id}),
        onError: notifyOnError(t),
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onSuccess()
        },
    })
    return _.isFunction(children) ? children(asyn_data) : children
}

const CodiDocumentSyncInfosGroup = ({
                                        title,
                                        description,
                                        onSuccess = NullAsDefault,
                                        onCancel = NullAsDefault,
                                        ...rest
                                    }) => {
    const t = useTranslate()

    const {item} = useContext(CodiDocumentContext)

    return (
        <AdvancedActionGroups.SimpleActionWrapper
            title={title}
            description={description}>
            <UseConfirmDeleteAction
                title={'Synchronize CODI information'}
                message={'Update, supplement CODI relationships: replacements'}
                apiInfo={docs_updateSyncInfoCodiDocument_Api}
                query={bindQueryParam({id: item?.id})}
                onSuccess={() => {
                    successNotify(NotificationActionTypes.UPDATE, t)
                    onCancel()
                }}>
                {({isLoading = false, confirm = NullAsDefault}) => (
                    <DefaultActionButton
                        onClick={() => {
                            confirm()
                        }}
                        isLoading={isLoading}
                        description={description}
                        title={'sync'}
                        label={'sync'}
                    />
                )}
            </UseConfirmDeleteAction>
        </AdvancedActionGroups.SimpleActionWrapper>
    )
}

const CodiDocumentRegenGroup = ({title, description, onSuccess = NullAsDefault}) => {
    const t = useTranslate()

    const {item} = useContext(CodiDocumentContext)

    const {isLoading = false, handleAsyncAction} = useAsyncAction({
        apiInfo: docs_reGenerateSyncCodiDocument_Api,
        query: bindQueryParam({id: item?.id}),
        onError: notifyOnError(t),
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onSuccess()
        },
    })

    return (
        <AdvancedActionGroups.StatusActionButton
            title={title}
            description={description}
            isLoading={!!isLoading}
            Component={DefaultActionButton}
            status={item?.sync_codi_document}
            onClick={() => {
                confirmActionDialog({
                    translate: t,
                    title: 'you want to regenerate the data',
                    content: 'regenerating the data will take a few minutes',
                    onOk: () => {
                        handleAsyncAction()
                    },
                })
            }}
            options={[
                {
                    value: -9,
                    label: 'pending',
                    className: 'text-primary',
                    buttonProps: {
                        type: 'primary',
                        label: 'revoke',
                    },
                },
                {
                    value: 0,
                    label: 'pending',
                    className: 'text-primary',
                    buttonProps: {
                        type: 'primary',
                        label: 'revoke',
                    },
                },
                {
                    value: 1,
                    label: 'success',
                    className: 'text-green-500',
                    buttonProps: {
                        type: 'primary',
                        label: 'create',
                    },
                },
                {
                    value: -1,
                    label: 'processing',
                    className: 'text-color-300',
                    buttonProps: {
                        disabled: true,
                        type: 'default',
                        label: 'progressing',
                    },
                },
                {
                    value: -2,
                    label: 'error',
                    className: 'text-red-600',
                    buttonProps: {
                        danger: true,
                        type: 'primary',
                        label: 'revoke',
                    },
                },
            ]}
        />
    )
}

const CodiDocumentFormatGroup = ({title, description, onSuccess = NullAsDefault}) => {
    const t = useTranslate()

    const {item} = useContext(CodiDocumentContext)

    const {
        isReformatLoading = false,
        handleAsyncAction: handleReformatDocument,
    } = useAsyncAction({
        apiInfo: docs_updateSyncReformat_Api,
        query: bindQueryParam({id: item?.id}),
        onError: notifyOnError(t),
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onSuccess()
        },
    })

    return (
        <AdvancedActionGroups.StatusActionButton
            title={title}
            description={description}
            isLoading={!!isReformatLoading}
            Component={DefaultActionButton}
            status={item?.sync_structure_format}
            onClick={() => {
                confirmActionDialog({
                    translate: t,
                    title: 'do you want to reformat document?',
                    content: 're-format action will take a few minutes',
                    onOk: () => {
                        handleReformatDocument()
                    },
                })
            }}
            options={[
                {
                    value: 0,
                    label: 'pending',
                    className: 'text-primary',
                    buttonProps: {
                        type: 'primary',
                        label: 'revoke',
                    },
                },
                {
                    value: 1,
                    label: 'success',
                    className: 'text-green-500',
                    buttonProps: {
                        type: 'primary',
                        label: 're-format',
                    },
                },
                {
                    value: -1,
                    label: 'processing',
                    className: 'text-color-300',
                    buttonProps: {
                        disabled: true,
                        type: 'default',
                        label: 'progressing',
                    },
                },
                {
                    value: -2,
                    label: 'error',
                    className: 'text-red-600',
                    buttonProps: {
                        danger: true,
                        type: 'primary',
                        label: 'revoke',
                    },
                },
                {
                    value: -9,
                    label: 'error',
                    className: 'text-red-600',
                    buttonProps: {
                        danger: true,
                        type: 'primary',
                        label: 'revoke',
                    },
                },
            ]}
        />
    )
}

const CodiDocumentMergeAmend = ({title, description, onSuccess = NullAsDefault}) => {
    const t = useTranslate()

    const {item} = useContext(CodiDocumentContext)

    const {isLoading = false, handleAsyncAction: handleMergeDocument} =
        useAsyncAction({
            apiInfo: docs_mergeCodiAmendments_Api,
            query: bindQueryParam({id: item?.id}),
            onError: notifyOnError(t),
            onSuccess: () => {
                successNotify(NotificationActionTypes.UPDATE, t)
                onSuccess()
            },
        })

    return (
        <AdvancedActionGroups.StatusActionButton
            title={title}
            description={description}
            isLoading={!!isLoading}
            Component={DefaultActionButton}
            status={item?.sync_apply_adj_document}
            onClick={() => {
                confirmActionDialog({
                    translate: t,
                    title: 'merge amendments',
                    content: 'are you sure',
                    onOk: () => {
                        handleMergeDocument()
                    },
                })
            }}
            options={[
                {
                    value: 0,
                    label: 'pending',
                    className: 'text-primary',
                    buttonProps: {
                        type: 'primary',
                        label: 'merge',
                    },
                },
                {
                    value: 1,
                    label: 'success',
                    className: 'text-green-500',
                    buttonProps: {
                        type: 'primary',
                        label: 'merge',
                    },
                },
                {
                    value: -1,
                    label: 'processing',
                    className: 'text-color-300',
                    buttonProps: {
                        disabled: true,
                        type: 'default',
                        label: 'progressing',
                    },
                },
            ]}
        />
    )
}

const CodiDocumentUpdateGroup = ({title, description}) => {
    const t = useTranslate()

    const {item} = useContext(CodiDocumentContext)

    const codi_document_id = item?.id

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: doc_editCodiDocument_Api,
        query: bindQueryParam({id: codi_document_id}),
        onError: notifyOnError(t),
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onCancel()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        apiInfo: docs_removeCodiDocument_Api,
        query: bindQueryParam({id: codi_document_id}),
        onError: notifyOnError(t),
        onSuccess: () => {
            successNotify(NotificationActionTypes.DELETE, t)
            onCancel()
        },
    })

    const {handleAsyncAction: setCodiDocuments} = useAsyncAction({
        apiInfo: doc_setPrevCodiDocument_Api,
        query: bindQueryParam({id: codi_document_id}),
        onError: notifyOnError(t),
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onCancel()
        },
    })

    const options = {
        overview: {
            label: 'overview',
            value: ActionTypes.UPDATE_OVERVIEW_INFO,
            description: 'codi information',
        },
        link: {
            label: 'update',
            value: ActionTypes.SET_PREV_CODI,
            description: 'updated doc replaced',
        },
        remove: {
            title: 'remove codi',
            label: 'remove',
            value: ActionTypes.REMOVE_CODI,
            danger: true,
            description: 'delete current codi',
            confirm: {
                title: 'delete',
                message: 'are you sure',
            },
        },
        publish: {
            label: 'public',
            name: 'public',
            value: !!item?.public,
            description: 'public codi description',
        },
        frozen: {
            label: 'frozen',
            name: 'frozen',
            value: !!item?.frozen,
            description: 'frozen codi description',
        },
        merge: {
            title: 'merge amendments',
            label: 'merge',
            value: ActionTypes.MERGE_AMENDMENTS,
            description: 'merge amendments description',
        },
    }

    const handleAction = (action) => {
        switch (action) {
            case ActionTypes.REMOVE_CODI:
                showConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onDelete()
                    },
                })
                break
            case ActionTypes.PUBLISH_CODI:
                showConfirmDialog({
                    translate: t,
                    onOk: () => {
                        onUpdate({
                            public: _.defaultTo(Number(!!!item?.public), 0),
                        })
                    },
                })
                break
            case ActionTypes.SET_PREV_CODI:
            case ActionTypes.UPDATE_OVERVIEW_INFO:
                setAction({
                    type: action,
                    value: item,
                })
                break
            default:
                break
        }
    }

    const modal = useMemo(() => {
        let params = {}
        let modal_params = {}
        let Component = NullAsDefault
        switch (action.type) {
            case ActionTypes.UPDATE_OVERVIEW_INFO:
                modal_params = {
                    title: (
                        <div className="text-center text-color-000 font-bold uppercase">
                            {t('update information')}
                        </div>
                    ),
                }
                params = {
                    item,
                }
                Component = (props) => (
                    <UseUpdateCodiDocument
                        onSuccess={onCancel}
                        codi_document_id={codi_document_id}>
                        {({handleAsyncAction}) => (
                            <UpdateInfo
                                onSubmit={(values) => {
                                    handleAsyncAction(values)
                                }}
                                {...props}
                            />
                        )}
                    </UseUpdateCodiDocument>
                )
                break
            case ActionTypes.SET_PREV_CODI:
                modal_params = {
                    title: (
                        <div className="text-center text-color-000 font-bold uppercase">
                            {t('replace for codi document')}
                        </div>
                    ),
                }
                params = {
                    item,
                    onSubmit: (values) => {
                        setCodiDocuments(values)
                    },
                }
                Component = PreCodiModal
                break
            default:
                break
        }
        return (
            <Modal
                className="custom-modal"
                footer={null}
                onCancel={onCancel}
                open={action.type}
                destroyOnHidden={true}
                {...modal_params}>
                <Component
                    {...{onCancel}}
                    {...params}
                />
            </Modal>
        )
    }, [action])

    return (
        <AdvancedActionGroups.ActionGroup
            title={title}
            description={description}>
            <AdvancedActionGroups.SimpleActionButton
                title={'information book'}
                label={'overview'}
                suffix={<BsChevronRight size={11}/>}
                onClick={() => {
                    handleAction(ActionTypes.UPDATE_OVERVIEW_INFO)
                }}
                description={'change codi basic information'}
            />

            <AdvancedActionGroups.SimpleActionButton
                title={'replaced codi'}
                label={'update'}
                suffix={<BsChevronRight size={11}/>}
                onClick={() => {
                    handleAction(ActionTypes.SET_PREV_CODI)
                }}
                description={'updated codi replaced'}
            />

            {[options.remove].map(
                ({value, title, description, confirm = false, ...rest}, index) => (
                    <AdvancedActionGroups.SimpleActionWrapper
                        key={index}
                        title={title}
                        description={description}>
                        <UseConfirmDeleteAction
                            {...(confirm ?? {})}
                            apiInfo={docs_removeCodiDocument_Api}
                            query={bindQueryParam({id: codi_document_id})}
                            onSuccess={() => {
                                successNotify(NotificationActionTypes.UPDATE, t)
                                onCancel()
                            }}>
                            {({isLoading = false, confirm = NullAsDefault}) => (
                                <DefaultActionButton
                                    onClick={() => {
                                        confirm()
                                    }}
                                    isLoading={isLoading}
                                    description={description}
                                    {...rest}
                                />
                            )}
                        </UseConfirmDeleteAction>
                    </AdvancedActionGroups.SimpleActionWrapper>
                )
            )}
            {[options.publish, options.frozen].map(
                ({name, value, ...rest}, index) => (
                    <UseUpdateCodiDocument
                        key={index}
                        onSuccess={onCancel}
                        codi_document_id={codi_document_id}>
                        {({isLoading, handleAsyncAction}) => (
                            <AdvancedActionGroups.SwitchActionButton
                                name={name}
                                disabled={!!isLoading}
                                isLoading={!!isLoading}
                                checked={!!Number(value ?? 0)}
                                onChange={(value) => {
                                    confirmActionDialog({
                                        translate: t,
                                        title: 'are you sure',
                                        content: 'are you sure to continue executing?',
                                        onOk: () => {
                                            handleAsyncAction({
                                                [name]: Number(value ?? 0),
                                            })
                                        },
                                    })
                                }}
                                {...rest}
                            />
                        )}
                    </UseUpdateCodiDocument>
                )
            )}
            <CodiDocumentRegenGroup
                title="re-generate codi document"
                description="re-generate codi description"
            />
            <CodiDocumentFormatGroup
                title="re-format document"
                description="re-format codi description"
            />
            <CodiDocumentSyncInfosGroup
                title="Synchronize CODI information"
                description="Update, supplement CODI relationships: replacements"
            />
            <CodiDocumentMergeAmend
                title="merge amendments"
                description="merge amendments description"
            />

            {/*{[options.merge].map(*/}
            {/*  ({ value, title, description, confirm = false, ...rest }, index) => (*/}
            {/*        <AdvancedActionGroups.SimpleActionWrapper*/}
            {/*            key={index}*/}
            {/*            title={title}*/}
            {/*            description={description}>*/}
            {/*          <UseConfirmDeleteAction*/}
            {/*              {...(confirm ?? {})}*/}
            {/*              apiInfo={docs_mergeCodiAmendments_Api}*/}
            {/*              query={bindQueryParam({ id: codi_document_id })}*/}
            {/*              onSuccess={() => {*/}
            {/*                successNotify(NotificationActionTypes.INFORMATION, t)*/}
            {/*                onCancel()*/}
            {/*              }}>*/}
            {/*            {({ isLoading = false, confirm = NullAsDefault }) => (*/}
            {/*                <DefaultActionButton*/}
            {/*                    onClick={() => {*/}
            {/*                      confirm()*/}
            {/*                    }}*/}
            {/*                    isLoading={isLoading}*/}
            {/*                    description={description}*/}
            {/*                    {...rest}*/}
            {/*                />*/}
            {/*            )}*/}
            {/*          </UseConfirmDeleteAction>*/}
            {/*        </AdvancedActionGroups.SimpleActionWrapper>*/}
            {/*  )*/}
            {/*)}*/}

            {modal}
        </AdvancedActionGroups.ActionGroup>
    )
}

export default CodiDocumentUpdateGroup
